/**
 * Safety actions: mute, block, report. See stores/safetyStore.ts for the state
 * and the read helpers (isBlocked, isHiddenUser, isMutedUser, canInteract).
 *
 * Signed in → user_mutes / user_blocks tables (RLS: own rows only) and the
 * get_safety_relations() RPC for "who blocked me". Signed out → localStorage.
 * Session-only targets (visitors without an account) are `k:<presenceKey>`
 * ids and are never persisted.
 */
import { supabase } from '../config/supabase';
import { getCurrentChannel } from './realtimeManager';
import { useSafetyStore, setSafety, sessionKey, type IdSet } from '../stores/safetyStore';

const LS_KEY = 'gathrxr.safety.v1';
export const SAFETY_CHANGED_EVENT = 'safety:changed';

export type ReportReason =
  | 'harassment' | 'hate_speech' | 'sexual_content' | 'spam'
  | 'cheating_griefing' | 'impersonation' | 'underage' | 'other';

export const REPORT_REASONS: { id: ReportReason; label: string; hint: string }[] = [
  { id: 'harassment', label: 'Harassment', hint: 'Bullying, threats, following or targeting someone' },
  { id: 'hate_speech', label: 'Hate speech', hint: 'Attacks on race, religion, gender, sexuality, disability…' },
  { id: 'sexual_content', label: 'Sexual content', hint: 'Sexual talk, gestures or imagery' },
  { id: 'spam', label: 'Spam', hint: 'Ads, scams, flooding chat' },
  { id: 'cheating_griefing', label: 'Cheating / griefing', hint: 'Ruining the world or games for others' },
  { id: 'impersonation', label: 'Impersonation', hint: 'Pretending to be someone else or staff' },
  { id: 'underage', label: 'Underage concern', hint: 'May be under the minimum age' },
  { id: 'other', label: 'Other', hint: 'Something else' },
];

/** A person in the room (or a chat author) that safety actions target. */
export interface SafetyTarget {
  /** Account id; null for a visitor without an account. */
  userId: string | null;
  /** Realtime presence key (per tab), when known. */
  key?: string | null;
  name: string;
}

/** The id we store for a target: account id, else a session-only key. */
export function targetId(t: SafetyTarget): string | null {
  if (t.userId) return t.userId;
  return t.key ? sessionKey(t.key) : null;
}

/** True when the target is the signed-in user or this very tab. */
export function isSelf(t: SafetyTarget, myPresenceKey?: string | null): boolean {
  const me = useSafetyStore.getState().me;
  if (t.userId && me && t.userId === me) return true;
  if (t.key && myPresenceKey && t.key === myPresenceKey) return true;
  return false;
}

// ---- local persistence (signed out) ----

function readLocal(): { muted: string[]; blocked: string[]; names: Record<string, string> } {
  try {
    const j = JSON.parse(localStorage.getItem(LS_KEY) || '{}');
    return { muted: Array.isArray(j.muted) ? j.muted : [], blocked: Array.isArray(j.blocked) ? j.blocked : [], names: j.names || {} };
  } catch {
    return { muted: [], blocked: [], names: {} };
  }
}
function writeLocal() {
  const s = useSafetyStore.getState();
  if (s.me) return;
  const persist = (set: IdSet) => Object.keys(set).filter((id) => !id.startsWith('k:'));
  try {
    localStorage.setItem(LS_KEY, JSON.stringify({ muted: persist(s.muted), blocked: persist(s.blocked), names: s.names }));
  } catch { /* private mode */ }
}
const toSet = (ids: string[]): IdSet => Object.fromEntries(ids.map((id) => [id, true as const]));

// ---- loading ----

let relationsInFlight: Promise<void> | null = null;

/** Re-read who I blocked and who blocked me (cheap; ids only). */
export function refreshRelations(): Promise<void> {
  if (!useSafetyStore.getState().me) return Promise.resolve();
  if (relationsInFlight) return relationsInFlight;
  relationsInFlight = (async () => {
    try {
      const { data, error } = await supabase.rpc('get_safety_relations');
      if (error || !Array.isArray(data)) return;
      const blocked: IdSet = {};
      const blockedBy: IdSet = {};
      for (const r of data as { user_id: string; i_blocked: boolean; blocked_me: boolean }[]) {
        if (r.i_blocked) blocked[r.user_id] = true;
        if (r.blocked_me) blockedBy[r.user_id] = true;
      }
      // Keep this tab's session-only blocks.
      const cur = useSafetyStore.getState();
      for (const id of Object.keys(cur.blocked)) if (id.startsWith('k:')) blocked[id] = true;
      const same = (a: IdSet, b: IdSet) => Object.keys(a).length === Object.keys(b).length && Object.keys(a).every((k) => b[k]);
      if (!same(blocked, cur.blocked) || !same(blockedBy, cur.blockedBy)) setSafety({ blocked, blockedBy });
    } finally {
      relationsInFlight = null;
    }
  })();
  return relationsInFlight;
}

async function loadFor(userId: string | null) {
  if (!userId) {
    const l = readLocal();
    setSafety({ me: null, muted: toSet(l.muted), blocked: toSet(l.blocked), blockedBy: {}, names: l.names, loaded: true });
    return;
  }
  setSafety({ me: userId, muted: {}, blocked: {}, blockedBy: {}, loaded: false });
  const [mutes] = await Promise.all([
    supabase.from('user_mutes').select('target_id').then((r) => r),
    refreshRelations(),
  ]);
  if (useSafetyStore.getState().me !== userId) return; // signed out meanwhile
  const muted = toSet(((mutes.data || []) as { target_id: string }[]).map((m) => m.target_id));
  setSafety({ muted, loaded: true });
}

let initialised = false;
let pollTimer: ReturnType<typeof setInterval> | null = null;

/** Idempotent. Runs on first import; follows sign-in / sign-out. */
export function initSafety() {
  if (initialised || typeof window === 'undefined') return;
  initialised = true;
  supabase.auth.getSession().then(({ data }) => loadFor(data.session?.user?.id || null)).catch(() => loadFor(null));
  supabase.auth.onAuthStateChange((_event, session) => {
    const id = session?.user?.id || null;
    if (id !== useSafetyStore.getState().me || !useSafetyStore.getState().loaded) void loadFor(id);
  });
  // Someone may block me while I'm in a world; their client announces a
  // change (no details) and every client re-reads its own relations. This
  // poll is the fallback for missed broadcasts and rooms without one.
  pollTimer = setInterval(() => {
    if (document.visibilityState === 'visible') void refreshRelations();
  }, 45_000);
}
initSafety();

// ---- realtime: "someone changed a block", no payload ----

const wiredChannels = new WeakSet<object>();
let refreshDebounce: ReturnType<typeof setTimeout> | null = null;

/** Listen on the current world channel for block changes. Safe to call often. */
export function attachSafetyChannel() {
  const channel = getCurrentChannel();
  if (!channel || wiredChannels.has(channel)) return;
  wiredChannels.add(channel);
  channel.on('broadcast', { event: SAFETY_CHANGED_EVENT }, () => {
    if (refreshDebounce) clearTimeout(refreshDebounce);
    // Spread the re-reads out a little so a room doesn't hit the RPC at once.
    refreshDebounce = setTimeout(() => void refreshRelations(), 300 + Math.random() * 900);
  });
}

function announceChange() {
  try {
    getCurrentChannel()?.send({ type: 'broadcast', event: SAFETY_CHANGED_EVENT, payload: {} });
  } catch { /* not in a world */ }
}

// ---- actions ----

function rememberName(id: string, name: string) {
  const s = useSafetyStore.getState();
  if (name && s.names[id] !== name) setSafety({ names: { ...s.names, [id]: name } });
}

export async function mute(t: SafetyTarget): Promise<void> {
  const id = targetId(t);
  if (!id || isSelf(t)) return;
  const s = useSafetyStore.getState();
  rememberName(id, t.name);
  setSafety({ muted: { ...s.muted, [id]: true } });
  if (s.me && t.userId) {
    const { error } = await supabase.from('user_mutes').upsert({ owner_id: s.me, target_id: t.userId }, { onConflict: 'owner_id,target_id', ignoreDuplicates: true });
    if (error) console.warn('[safety] mute not saved:', error.message);
  }
  writeLocal();
}

export async function unmute(t: SafetyTarget | string): Promise<void> {
  const id = typeof t === 'string' ? t : targetId(t);
  if (!id) return;
  const s = useSafetyStore.getState();
  const muted = { ...s.muted };
  delete muted[id];
  setSafety({ muted });
  if (s.me && !id.startsWith('k:')) {
    const { error } = await supabase.from('user_mutes').delete().eq('owner_id', s.me).eq('target_id', id);
    if (error) console.warn('[safety] unmute not saved:', error.message);
  }
  writeLocal();
}

export async function block(t: SafetyTarget): Promise<void> {
  const id = targetId(t);
  if (!id || isSelf(t)) return;
  const s = useSafetyStore.getState();
  rememberName(id, t.name);
  setSafety({ blocked: { ...s.blocked, [id]: true } });
  if (s.me && t.userId) {
    const { error } = await supabase.from('user_blocks').upsert({ owner_id: s.me, target_id: t.userId }, { onConflict: 'owner_id,target_id', ignoreDuplicates: true });
    if (error) console.warn('[safety] block not saved:', error.message);
    else announceChange();
  }
  writeLocal();
}

export async function unblock(t: SafetyTarget | string): Promise<void> {
  const id = typeof t === 'string' ? t : targetId(t);
  if (!id) return;
  const s = useSafetyStore.getState();
  const blocked = { ...s.blocked };
  delete blocked[id];
  setSafety({ blocked });
  if (s.me && !id.startsWith('k:')) {
    const { error } = await supabase.from('user_blocks').delete().eq('owner_id', s.me).eq('target_id', id);
    if (error) console.warn('[safety] unblock not saved:', error.message);
    else announceChange();
  }
  writeLocal();
}

export interface ReportInput {
  reportedId: string;
  reportedName?: string;
  worldId?: string | null;
  reason: ReportReason;
  details?: string;
  includeChat: boolean;
  platform: 'desktop' | 'mobile' | 'vr';
}

export type ReportResult = { ok: true; id?: string; emailed?: boolean } | { ok: false; error: string };

/**
 * File a report. Goes through /api/report (verifies the token, stores it as
 * the reporter, emails the team). If that endpoint isn't reachable (e.g. a
 * dev server proxying to a deploy that predates it) the report is stored
 * directly; the chat snapshot is captured by the database either way.
 */
export async function submitReport(input: ReportInput): Promise<ReportResult> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) return { ok: false, error: 'Sign in to report someone.' };
  if (input.reportedId === session.user.id) return { ok: false, error: "You can't report yourself." };
  const body = {
    reported_id: input.reportedId,
    world_id: input.worldId || null,
    reason: input.reason,
    details: (input.details || '').trim().slice(0, 2000) || null,
    include_chat: input.includeChat,
    platform: input.platform,
  };
  try {
    const res = await fetch('/api/report', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
      body: JSON.stringify(body),
    });
    const isJson = (res.headers.get('content-type') || '').includes('application/json');
    if (isJson) {
      const j = await res.json().catch(() => ({}));
      if (res.ok && j.ok) return { ok: true, id: j.id, emailed: !!j.emailed };
      if (res.status !== 404 && res.status !== 405) return { ok: false, error: j.error || `Couldn't send the report (${res.status}).` };
    }
  } catch { /* fall through to the direct insert */ }

  const { data, error } = await supabase
    .from('user_reports')
    .insert({ reporter_id: session.user.id, ...body })
    .select('id')
    .single();
  if (error) {
    const msg = error.message || '';
    return { ok: false, error: msg.startsWith('rate_limited:') ? msg.replace('rate_limited:', '').trim() : msg || "Couldn't send the report." };
  }
  return { ok: true, id: data?.id, emailed: false };
}

/** Names for a list of account ids (profiles are public). */
export async function fetchNames(ids: string[]): Promise<Record<string, string>> {
  const real = ids.filter((id) => /^[0-9a-f-]{36}$/i.test(id));
  if (!real.length) return {};
  const { data } = await supabase.from('profiles').select('id, username').in('id', real);
  return Object.fromEntries(((data || []) as { id: string; username: string | null }[]).map((p) => [p.id, p.username || 'Player']));
}

export const stopSafetyPolling = () => { if (pollTimer) clearInterval(pollTimer); pollTimer = null; };
